import type { BuildingGroupEntry } from '@uniticket/shared';

interface BuildingGroupTableProps {
  entries: BuildingGroupEntry[];
  onEntriesChange: (entries: BuildingGroupEntry[]) => void;
  readOnly?: boolean;
}

function renderExistingBoxes(value: string) {
  if (!value) return <span className="text-slate-400 italic text-xs">none</span>;
  const tokens = value.split(', ');
  return (
    <span className="font-mono text-xs">
      {tokens.map((t, i) => (
        <span key={i}>
          {i > 0 && <span className="text-slate-300">, </span>}
          {t.trim() === '' ? (
            <span className="italic text-slate-400">blank</span>
          ) : (
            <span className="text-slate-700">{t}</span>
          )}
        </span>
      ))}
    </span>
  );
}

export function BuildingGroupTable({ entries, onEntriesChange, readOnly }: BuildingGroupTableProps) {
  const totalVolumeMdu = entries.reduce((sum, e) => sum + e.volumeMdu, 0);

  function handleFieldChange(index: number, field: 'volumeMdu' | 'existingBoxes', value: string) {
    const updated = [...entries];
    if (field === 'volumeMdu') {
      const num = parseInt(value, 10);
      updated[index] = { ...updated[index], volumeMdu: isNaN(num) ? 0 : num };
    } else {
      updated[index] = { ...updated[index], existingBoxes: value };
    }
    onEntriesChange(updated);
  }

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <span className="text-sm font-semibold text-slate-700">Building Group Volumes</span>
        <span className="text-xs font-medium text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">
          {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Task (Address)</th>
              <th className="text-left px-3 py-2">Street</th>
              <th className="text-left px-3 py-2">Nr</th>
              <th className="text-left px-3 py-2">Postal</th>
              <th className="text-left px-3 py-2 whitespace-nowrap">Volume MDU</th>
              <th className="text-left px-3 py-2 whitespace-nowrap">Existing Boxes</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, idx) => (
              <tr
                key={`${entry.task}-${idx}`}
                className={`border-t border-slate-100 ${
                  entry.role === 'Main'
                    ? 'bg-brand-rose/5 border-l-2 border-brand-rose'
                    : 'border-l-2 border-transparent'
                }`}
              >
                <td className="px-3 py-2">
                  <span className={`text-xs font-semibold ${
                    entry.role === 'Main' ? 'text-brand-rose' : 'text-slate-500'
                  }`}>
                    {entry.role}
                  </span>
                </td>
                <td className={`px-3 py-2 text-slate-700 ${entry.role === 'Main' ? 'font-medium' : ''}`}>
                  {entry.task}
                </td>
                <td className="px-3 py-2 text-slate-600">{entry.streetName}</td>
                <td className="px-3 py-2 text-slate-600">{entry.houseNumberExtension || '—'}</td>
                <td className="px-3 py-2 text-slate-600 font-mono text-xs">{entry.postalCode}</td>
                <td className="px-3 py-2">
                  {readOnly ? (
                    <span className="font-mono text-slate-700">{entry.volumeMdu}</span>
                  ) : (
                    <input
                      type="number"
                      min="0"
                      value={entry.volumeMdu}
                      onChange={(e) => handleFieldChange(idx, 'volumeMdu', e.target.value)}
                      className="w-20 px-2 py-1 text-sm font-mono border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  {readOnly ? (
                    renderExistingBoxes(entry.existingBoxes)
                  ) : (
                    <div className="space-y-1">
                      <input
                        type="text"
                        value={entry.existingBoxes}
                        onChange={(e) => handleFieldChange(idx, 'existingBoxes', e.target.value)}
                        className="w-36 px-2 py-1 text-sm font-mono border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                      />
                      {entry.existingBoxes.includes(', ,') || entry.existingBoxes.startsWith(',') || entry.existingBoxes.endsWith(',') ? (
                        <div>{renderExistingBoxes(entry.existingBoxes)}</div>
                      ) : null}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200 bg-slate-50">
              <td colSpan={5} className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider text-slate-500">
                Total Volume MDU
              </td>
              <td className="px-3 py-2 font-mono font-bold text-slate-800">{totalVolumeMdu}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
